import type {
  PermissionGrantRecord,
  PermissionLevel,
  TeamRecord,
} from "@contract-manager/shared";
import { useEffect, useState } from "react";
import type { ApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { isSignedOutError } from "../auth/authErrors";
import { useI18n } from "../i18n/I18n";
import type { MessageKey } from "../i18n/messages";
import type { Translate } from "../i18n/translate";

/**
 * Who will be able to read a contract filed into this folder.
 *
 * Shown before the file is sent, because the answer is not guessable from the
 * folder's name and the cost of guessing wrong is a contract in front of the
 * wrong people. It matters most to somebody holding only UPLOAD on the folder:
 * they cannot open the folder to look, so this is the only way they learn what
 * they are contributing to.
 *
 * Only the grants that reach a contract are listed. A folder's own UPLOAD or
 * METADATA grant governs the folder, not what is inside it, so naming those
 * people here would say the opposite of what is true.
 */
const READS_CONTRACTS: readonly PermissionLevel[] = ["VIEW", "EDIT"];

export function UploadAudience({
  api,
  folderId,
}: {
  api: ApiClient;
  /** Empty for 未分類, which inherits from nothing. */
  folderId: string;
}) {
  const auth = useAuth();
  const { t } = useI18n();
  const [grants, setGrants] = useState<PermissionGrantRecord[] | null>(null);
  // Grants name a team by id; the list of teams is what turns that into
  // something worth reading. Fetched once, not per folder.
  const [teams, setTeams] = useState<TeamRecord[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const page = await api.listTeams();
        if (!cancelled) {
          setTeams(page.items);
        }
      } catch (caught) {
        if (isSignedOutError(caught)) {
          auth.reportSignedOut();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, auth]);

  useEffect(() => {
    if (folderId === "") {
      setGrants(null);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const page = await api.listPermissions("FOLDER", folderId);
        if (!cancelled) {
          setGrants(
            page.items.filter((grant) => READS_CONTRACTS.includes(grant.level)),
          );
        }
      } catch (caught) {
        if (isSignedOutError(caught)) {
          auth.reportSignedOut();
          return;
        }
        // A folder whose access list cannot be read is not a reason to block
        // an upload the API will accept; say nothing rather than alarm.
        if (!cancelled) {
          setGrants(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, auth, folderId]);

  const always = t("contracts.audience.always");

  if (folderId === "") {
    return (
      <p className="ds-text --small --muted">
        {t("contracts.audience.unfiled", { always })}
      </p>
    );
  }

  if (grants === null) {
    return null;
  }

  return (
    <p className="ds-text --small --muted">
      {grants.length === 0
        ? t("contracts.audience.noGrants", { always })
        : t("contracts.audience.withGrants", {
            always,
            names: grants
              .map((grant) => nameOf(grant, teams, t))
              .join(t("contracts.audience.separator")),
          })}
    </p>
  );
}

/**
 * A user grant already names an address, which reads fine. A team grant names
 * an id, and falls back to it when the team list has not arrived — better a
 * row that looks odd than one silently missing from a list whose whole purpose
 * is to be complete.
 */
function nameOf(
  grant: PermissionGrantRecord,
  teams: readonly TeamRecord[],
  t: Translate<MessageKey>,
): string {
  if (grant.principalType === "USER") {
    return grant.principalId;
  }
  const team = teams.find(
    (candidate) => candidate.teamId === grant.principalId,
  );
  return team === undefined
    ? grant.principalId
    : t("contracts.audience.team", { name: team.name });
}
